import {
  createEventCallbackRedirect,
  EVENT_TIMEOUT_IN_MS,
  hasMatchingDataLayerEvent,
  sendGAEvent,
} from '@noths/polaris-client-google-analytics';
import { EventsMap } from '@noths/polaris-client-next-redux';

import { CART_PATH } from 'src/constants/page';
import { Placement } from 'src/constants/recommendations';
import { addCrossSellProductToBasket, removeCrossSellProductFromBasket } from 'src/redux/basket';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { requestCrossSellProducts } from 'src/redux/richRelevance';
import { getSectionVisibleActionType } from 'src/redux/visibilityTracking';
import {
  generatePlacementProductFieldObject,
  generatePlacementProductListFieldObjects,
} from 'src/utils';
import { actions } from '../modules';
import {
  CROSS_SELL_MODAL_VISIBILITY_NAME,
  CrossSellAddCardToBasket,
  CrossSellAlgonomyResponseFalse,
  CrossSellAlgonomyResponseTrue,
  CrossSellCarouselSwipe,
  CrossSellCloseModalEvent,
  CrossSellContinueToCartEvent,
  CrossSellEcommerceEvent,
  CrossSellProductImpressions,
  CrossSellRemoveCardFromBasket,
} from './events';

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [getSectionVisibleActionType(CROSS_SELL_MODAL_VISIBILITY_NAME)]: (state) => {
    sendGAEvent<CrossSellEcommerceEvent>({
      ecommerce: null,
    });

    const { products, placementStrategy } = state.richRelevance[Placement.CrossSell];

    sendGAEvent<CrossSellProductImpressions>({
      event: 'product_impressions',
      event_action: 'Product Impressions',
      event_category: 'Rich Relevance',
      event_label: `${placementStrategy} : ${Placement.CrossSell}`,
      ecommerce: {
        impressions: generatePlacementProductListFieldObjects(
          {
            products: products!,
            placementName: Placement.CrossSell!,
            placementStrategy: placementStrategy!,
          },
          ['quantity'],
        ),
      },
    });
  },
  [addCrossSellProductToBasket.fulfilled.type]: (state, _, action) => {
    const { payload: code } = action as ReturnType<
      (typeof addCrossSellProductToBasket)['fulfilled']
    >;

    sendGAEvent<CrossSellEcommerceEvent>({
      ecommerce: null,
    });

    const { products, placementStrategy } = state.richRelevance[Placement.CrossSell];
    const product = products!.find((prod) => prod.code === code)!;

    sendGAEvent<CrossSellAddCardToBasket>({
      event: 'add_to_cart',
      event_action: 'Add to basket',
      event_category: 'Rich Relevance',
      event_label: `${placementStrategy} : ${Placement.CrossSell}`,
      algonomy_tracking_URL: product.linkURL,
      ecommerce: {
        add: {
          actionField: {
            list: `product - rich relevance - ${Placement.CrossSell} - ${placementStrategy}`,
          },
          products: [generatePlacementProductFieldObject(product)],
        },
      },
    });
  },
  [removeCrossSellProductFromBasket.fulfilled.type]: (state, _, action) => {
    const { payload: code } = action as ReturnType<
      (typeof addCrossSellProductToBasket)['fulfilled']
    >;

    sendGAEvent<CrossSellEcommerceEvent>({
      ecommerce: null,
    });

    const { products, placementStrategy } = state.richRelevance[Placement.CrossSell];
    const product = products!.find((prod) => prod.code === code)!;

    sendGAEvent<CrossSellRemoveCardFromBasket>({
      event: 'remove_from_cart',
      event_action: 'Remove from basket',
      event_category: 'Rich Relevance',
      event_label: `${placementStrategy} : ${Placement.CrossSell}`,
      ecommerce: {
        remove: {
          actionField: {
            list: `product - rich relevance - ${Placement.CrossSell} - ${placementStrategy}`,
          },
          products: [generatePlacementProductFieldObject(product)],
        },
      },
    });
  },
  [actions.onContinueToCart.type]: ({ richRelevance }, __) => {
    const { placementStrategy } = richRelevance[Placement.CrossSell];
    const eventCallback = createEventCallbackRedirect(CART_PATH);

    sendGAEvent<CrossSellContinueToCartEvent>({
      event: 'custom_event',
      event_action: 'Basket click',
      event_category: 'Rich Relevance',
      eventCallback,
      eventTimeout: EVENT_TIMEOUT_IN_MS,
      event_label: `${placementStrategy} : ${Placement.CrossSell}`,
    });
  },
  [actions.closeModal.type]: ({ richRelevance }) => {
    const { placementStrategy } = richRelevance[Placement.CrossSell];

    sendGAEvent<CrossSellCloseModalEvent>({
      event: 'custom_event',
      event_action: 'Close modal click',
      event_category: 'Rich Relevance',
      event_label: `${placementStrategy} : ${Placement.CrossSell}`,
    });
  },
  [requestCrossSellProducts.fulfilled.type]: (_, __, action) => {
    const { payload } = action as ReturnType<(typeof requestCrossSellProducts)['fulfilled']>;

    const eventTrue: CrossSellAlgonomyResponseTrue = {
      event: 'custom_event',
      event_action: 'Rich Relevance',
      event_category: 'API Response',
      event_label: 'True',
    };

    const eventFalse: CrossSellAlgonomyResponseFalse = {
      ...eventTrue,
      event_label: 'False',
    };

    const hasProducts = payload?.products.length;

    if (hasProducts && !hasMatchingDataLayerEvent(eventTrue)) {
      sendGAEvent<CrossSellAlgonomyResponseTrue>(eventTrue);

      return;
    }

    if (!hasProducts && !hasMatchingDataLayerEvent(eventFalse)) {
      sendGAEvent<CrossSellAlgonomyResponseFalse>(eventFalse);
    }
  },
  [requestCrossSellProducts.rejected.type]: () => {
    const event: CrossSellAlgonomyResponseFalse = {
      event: 'custom_event',
      event_action: 'Rich Relevance',
      event_category: 'API Response',
      event_label: 'False',
    };

    if (!hasMatchingDataLayerEvent(event)) {
      sendGAEvent(event);
    }
  },
  [actions.onCarouselSwipe.type]: ({ richRelevance }, __, action) => {
    const { payload: direction } = action as ReturnType<(typeof actions)['onCarouselSwipe']>;
    const { placementStrategy } = richRelevance[Placement.CrossSell];

    const eventAction: CrossSellCarouselSwipe['event_action'] =
      direction === 'Right' ? 'Swipe next' : 'Swipe previous';

    sendGAEvent<CrossSellCarouselSwipe>({
      event: 'custom_event',
      event_action: eventAction,
      event_category: 'Rich Relevance',
      event_label: `${placementStrategy} : ${Placement.CrossSell}`,
    });
  },
};
